import { Injectable, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { Observable, of, Subscription, BehaviorSubject, combineLatest, filter, map, take } from 'rxjs';
import { MessageService } from 'primeng/api';

import { AddAllRecipientsToListsContext } from '../../../api/distributions/model/add-all-recipients-to-lists.api.model';
import { OutletsPublicApi } from '../../../api/targeting/outlets-public.api';
import { DistributionApi } from '../../../api/distributions/distribution.api';
import { EntityType } from '../../../api/targeting/enum/entity.type.enum';

import { DiscoverRoutingService } from '../../../Discover/DiscoverRouter/services/discover-routing.service';
import { TargetingListList } from '../../../Discover/Targeting/models/targeting-list-list.model';
import { TargetingResultModel } from '../../../Discover/Targeting/models/targeting-result.model';
import { TargetingSearchService } from '../../../Discover/Targeting/services/targeting-search.service';
import { TargetingListService } from '../../../Discover/Targeting/services/targeting-list.service';
import { TargetingList } from '../../../Discover/Targeting/models/targeting-list.model';

import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';

import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { ConfirmDialogComponent } from '../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { MonitoringRoutingService } from '../../../Monitor/Core/services/monitoring-routing.service';

@Injectable()
export class ListPickerService implements OnDestroy {
    public readonly MAX_CONTACTS = 10000;

    private subscriptions: Subscription[] = [];

    private targetingLists: TargetingListList[] = null;
    private focusedList: TargetingList = null;

    private workingLists: TargetingListList[] = [];
    private workingLists$: BehaviorSubject<TargetingListList[]> = new BehaviorSubject<TargetingListList[]>(this.workingLists);

    private addAllRecipientsToListsContext: AddAllRecipientsToListsContext;

    private readonly workingListsSessionKey: string = 'targeting-working-lists';
    private workingListsLock: boolean = false;
    private workingListsLock$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    selectedEntities: TargetingResultModel[] = null;
    selectedEntities$: BehaviorSubject<TargetingResultModel[]> = new BehaviorSubject<TargetingResultModel[]>(this.selectedEntities);

    allResultsSelected: boolean = false;
    total: number = 0;
    total$: BehaviorSubject<number> = new BehaviorSubject<number>(this.total);

    public useLocking: boolean = false;

    private listMembershipsChanged$: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    constructor(
        private discoverRoutingService: DiscoverRoutingService,
        private monitoringRoutingService: MonitoringRoutingService,
        private localizationService: LocalizationService,
        private messageService: MessageService,
        private targetingListService: TargetingListService,
        private targetingSearchService: TargetingSearchService,
        private coreDialogService: CoreDialogService,
        private outletsPublicApi: OutletsPublicApi,
        private distributionApi: DistributionApi
    ) {
        this.subscriptions.push(
            this.discoverRoutingService.getCurrentRoute().subscribe(
                route => {
                    if (!this.workingListsLock) {
                        this.setWorkingLists([]);
                    }
                }
            ),
            this.monitoringRoutingService.getCurrentRoute().subscribe(
                route => {
                    if (!this.workingListsLock) {
                        this.setWorkingLists([]);
                    }
                }
            ),
            this.targetingListService.currentResource$.subscribe(
                list => {
                    this.focusedList = list;
                }
            )
        );
    }


    getWorkingLists(): Observable<TargetingListList[]> {
        return this.workingLists$;
    }

    getWorkingListsLock(): Observable<boolean> {
        return this.workingListsLock$;
    }

    addSelectedEntitiesToLists(lists: TargetingListList[], outletUuid?: string): Promise<void> {
        return this.addEntitiesToLists(this.selectedEntities, lists, outletUuid);
    }

    addSelectedEntitiesToWorkingLists(outletUuid?: string): Promise<void> {
        return this.addEntitiesToLists(this.selectedEntities, this.workingLists, outletUuid);
    }

    addEntitiesToLists(entities: TargetingResultModel[], lists: TargetingListList[], outletUuid?: string): Promise<void> {
        let apiCall;
        if (this.allResultsSelected) {
            if (this.total > this.MAX_CONTACTS) {
                return this.warnExceedingAddLimit();
            }

            // TODO: add enum as modes to distinguish between centeral panel, staff search and recipient search add to list operations

            // Add all staff of a specific outlet to lists
            if (outletUuid) {
                apiCall = this.outletsPublicApi.addAllStaffToLists(
                    lists.map(x => x.id),
                    outletUuid
                );
            }

            // Add all distribution recipients to lists
            if (this.addAllRecipientsToListsContext) {
                this.addAllRecipientsToListsContext.apiBody.listIds = lists.map(list => list.id);

                apiCall = this.distributionApi.addAllRecipientsToLists(
                    this.addAllRecipientsToListsContext.distributionId,
                    this.addAllRecipientsToListsContext.apiBody
                );
            }

            if (!apiCall) {
                apiCall = this.targetingListService.addSearchResultsToList(
                    lists.map(x => x.id),
                    this.targetingSearchService.getCurrentSearchContext());
            }
        } else {
            apiCall = this.targetingListService.addEntitiesToLists(
                lists.map(x => x.id),
                entities.map(x => x.toEntityIdModel())
            );
        }
        const isOutletOnly = entities.every(x => x.entityType === EntityType.OUTLET);

        return new Promise<void>((resolve => {
            apiCall.subscribe({
                next: () => {
                    if (this.focusedList) {
                        // refresh the search result if the current focused list has been changed
                        lists.forEach(list => {
                            if (list.id === this.focusedList.id) {
                                this.targetingSearchService.doSearch(true, true);
                            }
                        });
                    }

                    // It could be safer to refetch from the backend
                    // If entities get removed from the list in another tab, then this could be inaccurate
                    entities.forEach(result => {
                        lists.forEach(list => {
                            if (result.listMemberships && !result.listMemberships.find(x => x.id === list.id)) {
                                result.listMemberships.push(new TargetingList({
                                    id: list.id,
                                    name: list.name
                                }));
                            }
                        });
                    });
                    this.listMembershipsChanged$.next(null);

                    // TODO : This should be clickable to navigate to the list
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: this.localizationService.transform('Discover.listBuilder.addToListSuccessMessageSummary' + (isOutletOnly ? '.outlet' : '')),
                        detail: lists.length === 1 ?
                            this.localizationService.transform('Discover.listBuilder.addToListSuccessMessage' + (isOutletOnly ? '.outlet' : ''), {
                                count: this.total,
                                listName: lists[0].name
                            })
                            : this.localizationService.transform('Discover.listBuilder.addToListsSuccessMessage' + (isOutletOnly ? '.outlet' : ''), {
                                contactCount: this.total,
                                listCount: lists.length
                            })
                    });
                    this.addAllRecipientsToListsContext = null;
                    resolve();
                },
                error: (error) => {
                    if (error instanceof HttpErrorResponse) {
                        const httpError = <HttpErrorResponse> error;
                        // TODO : coded error
                        if ((<string>error.error).indexOf('size to exceed the maximum of') > 0) {
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                detail: 'Discover.listBuilder.error.maxSize'
                            });
                        }
                    }
                    this.addAllRecipientsToListsContext = null;
                    resolve();
                }
            });
        }));
    }

    warnExceedingAddLimit(): Promise<void> {
        return new Promise<void>(resolve => {
            this.coreDialogService.open(ConfirmDialogComponent, {
                inputs: {
                    alertType: messageSeverityEnum.warning,
                    heading: this.localizationService.transform('Discover.listBuilder.errorDialog.addToList.header'),
                    message: this.localizationService.transform('Discover.listBuilder.errorDialog.addToList.message'),
                    alertMessage: this.localizationService.transform('Discover.listBuilder.errorDialog.addToList.warning'),
                    alertMessageList: [
                        this.localizationService.transform(
                            'Discover.listBuilder.errorDialog.addToList.warningMessage',
                            { contacts: this.total }
                        )
                    ],
                    okLabel: this.localizationService.transform('Discover.listBuilder.errorDialog.addToList.acknowledge'),
                    showCancel: false,
                    confirmAction: () => {
                        resolve();
                        return of(true);
                    }
                }
            });
        });
    }

    getListMembershipsChanged() {
        return this.listMembershipsChanged$;
    }

    addWorkingList(list: TargetingListList) {
        // Don't add a list if it's already in the working list
        if (this.workingLists.find(l => l.id === list.id)) {
            return;
        }
        this.setWorkingLists([...this.workingLists, list]);
    }

    removeWorkingListByIndex(index: number) {
        this.setWorkingLists(this.workingLists.filter((list, i) => i !== index));
        if (this.workingLists.length === 0) {
            this.setWorkingListsLock(false);
        }
    }

    setWorkingLists(lists: TargetingListList[]): void {
        this.workingLists = lists;
        this.workingLists$.next(this.workingLists);
        if (this.useLocking) {
            this.updateLockedWorkingLists();
        }
    }

    private updateLockedWorkingLists(): void {
        if (this.workingListsLock) {
            sessionStorage.setItem(this.workingListsSessionKey, JSON.stringify(this.workingLists.map(list => list.id)));
        } else if (sessionStorage.getItem(this.workingListsSessionKey)) {
            sessionStorage.removeItem(this.workingListsSessionKey);
        }
    }

    public loadWorkingListsFromSession(): void {
        const raw = sessionStorage.getItem(this.workingListsSessionKey);
        if (raw) {
            let ids;
            try {
                ids = JSON.parse(raw);
            } catch (err) {
                // handled by the if statement below because it covers more cases
            }
            if (!ids || ids.length < 1) {
                this.clearWorkingLists();
                return;
            }

            // if the target list already exists, grab the list, we don't need to make another API call to get the list.. (as it was previously done)
            combineLatest([
                this.targetingListService.all$.pipe(filter(x => !!x)),
            ]).pipe(
                // take(1) => we only need this to return 1 time.. not everytime we do targetingLists$.next
                take(1),
                map(([lists]) => {
                    // from the list of IDs we got from session, map it with the target list to be used as filter chips
                    const workingLists = ids.map(id => lists.find(list => list.id === id)).filter(list => list);
                    this.setWorkingLists(workingLists);
                    this.setWorkingListsLock(true);
                })
            ).subscribe();
        }
    }

    clearWorkingLists() {
        this.workingLists = [];
        this.workingLists$.next(this.workingLists);
        if (this.useLocking) {
            this.workingListsLock = false;
            this.workingListsLock$.next(this.workingListsLock);
            this.updateLockedWorkingLists();
        }
    }

    toggleWorkingListsLock() {
        this.setWorkingListsLock(!this.workingListsLock);
    }

    setWorkingListsLock(locked: boolean): void {
        if (this.useLocking) {
            this.workingListsLock = locked;
            this.workingListsLock$.next(this.workingListsLock);
            this.updateLockedWorkingLists();
        }
    }

    setSelectedEntities(selectedEntities: TargetingResultModel[]) {
        this.selectedEntities = selectedEntities;
        this.selectedEntities$.next(this.selectedEntities);
        if (!this.allResultsSelected) {
            this.total = selectedEntities.length;
            this.total$.next(this.total);
        }
    }

    setAllResultsSelected(allResultsSelected: boolean, total: number = 0) {
        this.allResultsSelected = allResultsSelected;
        this.total = total;
        this.total$.next(this.total);
    }

    setAddRecipientsToListsContext(context: AddAllRecipientsToListsContext) {
        this.addAllRecipientsToListsContext = context;
    }

    getSelectedEntities(): Observable<TargetingResultModel[]> {
        return this.selectedEntities$;
    }

    getTotal(): Observable<number> {
        return this.total$;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
